/**
 * Client-side place text search using the Google Maps JavaScript API (Place class, new API).
 * Uses Place.searchByText() instead of legacy PlacesService.textSearch().
 * All requests go directly to maps.googleapis.com; no request is sent to the app origin.
 * Requires REACT_APP_GOOGLE_PLACES_API_KEY and the Places library (same as autocomplete).
 */
import { loadGoogleMapsScript } from './loadGoogleMapsScript';

const API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

/**
 * Normalize a new Place object to the shape used by the app.
 * @param {google.maps.places.Place} place
 * @param {{ includeTypes?: boolean, includeRating?: boolean }} [opts]
 * @returns {{ id: string, displayName: { text: string }, location: { latitude: number, longitude: number }, types?: string[], rating?: number, userRatingCount?: number } | null}
 */
function normalizePlace(place, opts = {}) {
  if (!place?.id) return null;
  const loc = place.location;
  const lat = loc?.lat?.() ?? loc?.lat;
  const lng = loc?.lng?.() ?? loc?.lng;
  if (lat == null || lng == null) return null;
  const name = place.displayName?.text ?? place.displayName ?? '';
  const out = {
    id: place.id,
    displayName: { text: String(name) },
    location: { latitude: Number(lat), longitude: Number(lng) },
  };
  if (opts.includeTypes) {
    if (Array.isArray(place.types)) out.types = place.types.map((t) => String(t));
    if (place.primaryType != null) out.primaryType = String(place.primaryType);
  }
  if (opts.includeRating) {
    if (place.rating != null) out.rating = Number(place.rating);
    if (place.userRatingCount != null) out.userRatingCount = Number(place.userRatingCount);
  }
  return out;
}

/**
 * Try backend /api/places-search first (cached on deployed); fall back to client-side Google API.
 * Use this for grocery and gym searches so deployed map loads fast from cache.
 *
 * @param {{ textQuery: string, lat: number, lng: number, pageSize?: number, includeTypes?: boolean, includeRating?: boolean, includedType?: string, useStrictTypeFiltering?: boolean }} params
 * @returns {Promise<{ places: Array }>}
 */
export async function searchPlacesTextPreferred(params) {
  const { lat, lng, textQuery, pageSize = 10, includedType, useStrictTypeFiltering = false } = params;
  const query = String(textQuery || '').trim();
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!query || Number.isNaN(latNum) || Number.isNaN(lngNum)) {
    return { places: [] };
  }
  const q = new URLSearchParams({
    lat: latNum,
    lng: lngNum,
    textQuery: query,
    pageSize: String(Math.min(20, Math.max(1, Number(pageSize) || 10))),
  });
  if (includedType) q.set('includedType', includedType);
  if (useStrictTypeFiltering) q.set('useStrictTypeFiltering', 'true');
  try {
    const res = await fetch(`/api/places-search?${q}`);
    if (res.ok) {
      const data = await res.json();
      return { places: Array.isArray(data.places) ? data.places : [] };
    }
  } catch (_) {
    // fall through to client-side search
  }
  return searchPlacesText(params);
}

/**
 * Run a place text search using the Google Maps JavaScript API (browser).
 * Does not call the app backend; requests go to Google.
 *
 * @param {{ textQuery: string, lat: number, lng: number, pageSize?: number, includeTypes?: boolean, includeRating?: boolean, includedType?: string, useStrictTypeFiltering?: boolean }} params
 * @returns {Promise<{ places: Array<{ id: string, displayName: { text: string }, location: { latitude: number, longitude: number }, types?: string[], rating?: number, userRatingCount?: number }>}>}
 */
export function searchPlacesText({
  textQuery,
  lat,
  lng,
  pageSize = 10,
  includeTypes = false,
  includeRating = false,
  includedType,
  useStrictTypeFiltering = false,
}) {
  const query = String(textQuery || '').trim();
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!query || Number.isNaN(latNum) || Number.isNaN(lngNum)) {
    return Promise.resolve({ places: [] });
  }

  const maxResults = Math.min(Math.max(1, Number(pageSize) || 10), 20);
  const fields = ['displayName', 'location'];
  if (includeTypes) fields.push('types', 'primaryType');
  if (includeRating) fields.push('rating', 'userRatingCount');

  return loadGoogleMapsScript(API_KEY)
    .then((placesLib) => {
      const Place = placesLib?.Place;
      if (!Place?.searchByText) return { places: [] };

      const request = {
        textQuery: query,
        fields,
        locationBias: { lat: latNum, lng: lngNum },
        maxResultCount: maxResults,
        region: 'us',
      };
      if (includedType) request.includedType = String(includedType);
      if (useStrictTypeFiltering) request.useStrictTypeFiltering = true;

      return Place.searchByText(request).then(({ places: rawPlaces = [] }) => {
        const places = rawPlaces.map((p) => normalizePlace(p, { includeTypes, includeRating })).filter(Boolean);
        return { places };
      });
    })
    .catch(() => ({ places: [] }));
}

